import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {safelyGetFilterByKey} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {LAST_UPDATED_VALUE_OPTIONS} from 'shells/control-bar/constants';
import {ExpandedFilterBar} from 'shells/control-bar/expanded-filter-bar';
import {getArchivedValue, getLastUpdatedValue} from 'shells/control-bar/helpers';
import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
// @ts-expect-error - imported from .js/.jsx file
import {AudienceIcon} from 'shells/icon/svg/audience-icon';
import {Link} from 'shells/link/link';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import type {AudienceStub as Audience} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {GetSessionUser} from '../../core/get-session-user';
import {PageContainer, PageTitleBar} from '../../layout';

import {checkIsFiltering} from '../email-marketing-helpers';
import {AudienceListQuery} from '../graphql-wrappers/audience/audience-list-query';
import {AudienceMutations} from '../graphql-wrappers/audience/audience-mutations';
import {getAudienceListQueryVariables} from '../graphql-wrappers/helpers';
import {MarketingStatsBanner} from '../marketing-stats-banner/marketing-stats-banner';

import {AudiencesList} from './audiences-list';
import {UnsubscribeModal} from './unsubscribe-modal/unsubscribe-modal';

import styles from './audiences-list-page.module.css';

type OwnProps = ContextRouter & {
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;

    urlParams: any;
    onUpdateFilters: (arg1: FilterObject) => void;
    onRemoveAllFilters: () => void;
};

type Props = OwnProps & {
    isLoading: boolean;
    isFiltering: boolean;
    audiences: Audience[];
    onCreateAudience: (arg1: string) => Promise<any>;
    onDeleteAudience: (arg1: string) => Promise<any>;
    onRestoreAudience: (arg1: string) => Promise<any>;
    error: string | null | undefined;
    searchValue: string;
    userId: string;
    showPreferenceCenterLink: boolean;
};

function AudienceListPageComponent(props: Props) {
    const [isAddingNewAudience, setIsAddingNewAudience] = React.useState<boolean>(false);
    const [isSavingNewAudience, setIsSavingNewAudience] = React.useState<boolean>(false);
    const [isImportingUnsubscribes, setIsImportingUnsubscribes] = React.useState<boolean>(false);

    // Support ?create=true from command palette AI action
    React.useEffect(() => {
        if (props.urlParams.create === 'true') {
            setIsAddingNewAudience(true);
            // Clear the param so refresh doesn't re-trigger
            props.onParamChange({create: undefined}, true);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const isFilteringOnCreatorUserId = Boolean(
        // @ts-expect-error
        props.activeFilters.find((filter) => {
            return Object.keys(filter)[0] === 'creatorUserId';
        })
    );

    const isShowingArchived = safelyGetFilterByKey(props.activeFilters, 'archived') === 'true';
    const isFilteringOnPublic = safelyGetFilterByKey(props.activeFilters, 'public') === 'true';
    const isFiltering = checkIsFiltering(props.activeFilters, props.searchValue);

    return (
        <PageContainer
            title='Audiences'
            pageTitleBar={
                <PageTitleBar
                    title='Audiences'
                    subheaderPrimaryText='Targeted lists of contacts for your email outreach'
                    buttons={
                        <>
                            {props.showPreferenceCenterLink && (
                                <Link
                                    variant='button-secondary'
                                    icon={AudienceIcon}
                                    size='big'
                                    to='/marketing/email/preference-center'
                                    // @ts-expect-error
                                    as={RoutedLink}
                                >
                                    Manage preference center
                                </Link>
                            )}
                            <Button
                                variant='secondary'
                                size='big'
                                onClick={() => {
                                    setIsImportingUnsubscribes(true);
                                }}
                            >
                                Import unsubscribes
                            </Button>
                            <Button
                                variant='primary'
                                size='big'
                                onClick={() => {
                                    setIsAddingNewAudience(true);
                                }}
                            >
                                Create new audience
                            </Button>
                        </>
                    }
                />
            }
            controlBar={
                <ListPageControlBar
                    archiveTab={{
                        isActive: isShowingArchived,
                        onChange: () => {
                            props.onUpdateFilters({
                                archived: isShowingArchived ? null : true,
                            });
                        },
                    }}
                    leftSection={
                        <ExpandedFilterBar
                            lastUpdatedFilterProps={{
                                lastUpdatedFilter: safelyGetFilterByKey(
                                    props.activeFilters,
                                    'lastUpdated'
                                ),
                                // @ts-expect-error
                                lastUpdatedOptions: LAST_UPDATED_VALUE_OPTIONS,
                            }}
                            justMineFilterProps={{
                                isFilteringOnCreatorUserId: isFilteringOnCreatorUserId,
                                userId: props.userId,
                            }}
                            publicFilterProps={{
                                isFilteringOnPublic: isFilteringOnPublic,
                            }}
                            searchFilterProps={{
                                searchValue: props.searchValue,
                                onUpdateSearch: (newSearchValue) => {
                                    props.onParamChange({q: newSearchValue}, true);
                                },
                            }}
                            noun='audience'
                            count={props.audiences.length}
                            onUpdateFilters={props.onUpdateFilters}
                            isFiltering={isFiltering}
                            onRemoveAllFilters={props.onRemoveAllFilters}
                            activeFilters={props.activeFilters}
                        />
                    }
                />
            }
        >
            <MarketingStatsBanner isImportUnsubscribesBannerNeeded={true} />
            <div className={styles.container}>
                <AudiencesList
                    isLoading={props.isLoading}
                    isFiltering={props.isFiltering}
                    isShowingArchived={isShowingArchived}
                    audiences={props.audiences}
                    onDeleteAudience={props.onDeleteAudience}
                    onRestoreAudience={props.onRestoreAudience}
                    error={props.error}
                    setIsAddingNewAudience={setIsAddingNewAudience}
                />
            </div>
            {isImportingUnsubscribes ? (
                <UnsubscribeModal isOpen={true} onClose={() => setIsImportingUnsubscribes(false)} />
            ) : null}
            {isAddingNewAudience ? (
                <SingleInputModal
                    requireValue={true}
                    buttonText='Create'
                    title='Name this audience'
                    subtitle='Add a descriptive name so you can find it later'
                    placeholder='e.g. Current customers'
                    isSaving={isSavingNewAudience}
                    onSave={(newName) => {
                        setIsSavingNewAudience(true);
                        // @ts-expect-error
                        props.onCreateAudience(newName).then((newAudience) => {
                            setIsAddingNewAudience(false);
                            setIsSavingNewAudience(false);

                            if (!newAudience) return;

                            appHistory.push(newAudience.htmlUrlPath);
                        });
                    }}
                    onClose={() => {
                        setIsAddingNewAudience(false);
                    }}
                />
            ) : null}
        </PageContainer>
    );
}

export function AudienceListPage(props: OwnProps) {
    // We've hardcoded the server's sort for now - table sorting is currently
    // handled on the client and stored in the URL
    const sortValue = 'MODIFIED_TIME_DESC';
    const searchValue = props.urlParams.q || '';
    const isArchived = getArchivedValue(props.activeFilters);
    const isPublic = safelyGetFilterByKey(props.activeFilters, 'public') === 'true';

    // @ts-expect-error
    const creatorUserIdFilter = props.activeFilters.find((filter) => {
        return Object.keys(filter)[0] === 'creatorUserId';
    });
    const creatorUserId = creatorUserIdFilter ? creatorUserIdFilter.creatorUserId : undefined;

    const queryVariables = getAudienceListQueryVariables({
        isArchived,
        sort: sortValue,
        search: searchValue,
        creatorUserId,
        lastUpdatedTime: getLastUpdatedValue(props.activeFilters),
        onlyPublic: isPublic,
    });

    const lastUpdatedTime = getLastUpdatedValue(props.activeFilters);

    return (
        <GetSessionUser>
            {/* @ts-expect-error */}
            {({user}) => {
                return user ? (
                    <AudienceMutations>
                        {({onCreate, onDelete, onRestore}) => (
                            <AudienceListQuery
                                isArchived={isArchived}
                                sortOrder={sortValue}
                                searchQuery={searchValue}
                                lastUpdatedTime={lastUpdatedTime}
                                creatorUserId={creatorUserId ? user.id : undefined}
                                onlyPublic={isPublic}
                            >
                                {({audiences, isLoading, error, isFiltering}) => (
                                    <AudienceListPageComponent
                                        {...props}
                                        audiences={audiences}
                                        error={error}
                                        isFiltering={isFiltering}
                                        isLoading={isLoading}
                                        // @ts-expect-error
                                        onCreateAudience={(name) => {
                                            return onCreate(name, queryVariables);
                                        }}
                                        // @ts-expect-error
                                        onRestoreAudience={(audienceId) => {
                                            return onRestore(audienceId, queryVariables);
                                        }}
                                        // @ts-expect-error
                                        onDeleteAudience={(audienceId) => {
                                            return onDelete(audienceId, queryVariables);
                                        }}
                                        sortValue={sortValue}
                                        searchValue={searchValue}
                                        userId={user.id}
                                        showPreferenceCenterLink={user.permissions.canAccessSetup}
                                    />
                                )}
                            </AudienceListQuery>
                        )}
                    </AudienceMutations>
                ) : null;
            }}
        </GetSessionUser>
    );
}
